import fs from 'node:fs'
import path from 'node:path'

export interface EnvVar {
  key: string
  value: string
  comment?: string
}

export interface EnvSection {
  header: string
  vars: EnvVar[]
}

export function readEnvVar(cwd: string, key: string): string | undefined {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) {
    return undefined
  }

  const content = fs.readFileSync(envPath, 'utf-8')
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#') || !trimmed.includes('=')) {
      continue
    }

    const [lineKey, ...rest] = trimmed.split('=')
    if (lineKey?.trim() !== key) {
      continue
    }

    return parseEnvValue(rest.join('='))
  }

  return undefined
}

function parseEnvValue(rawValue: string): string {
  const value = rawValue.trim()
  const quote = value[0]
  if (quote === '"' || quote === "'") {
    const closingQuoteIndex = value.indexOf(quote, 1)
    if (closingQuoteIndex !== -1) {
      return value.slice(1, closingQuoteIndex).trim()
    }
  }

  return value
    .replace(/\s+#.*$/, '')
    .replace(/^['"]|['"]$/g, '')
    .trim()
}

/**
 * Safely append environment variables to .env.local.
 * Never overwrites existing values — only adds missing keys.
 * Keys listed in `overwrite` will be updated even if they already exist.
 */
export function appendEnvVars(
  cwd: string,
  sections: EnvSection[],
  overwrite?: Set<string>
): { added: string[]; skipped: string[]; updated: string[] } {
  const envPath = path.join(cwd, '.env.local')
  let existing = fs.existsSync(envPath) ? fs.readFileSync(envPath, 'utf-8') : ''

  const existingKeys = new Set(
    existing
      .split('\n')
      .filter((line) => line.trim() && !line.trim().startsWith('#'))
      .map((line) => line.split('=')[0]?.trim())
      .filter(Boolean)
  )

  const added: string[] = []
  const skipped: string[] = []
  const updated: string[] = []
  const lines: string[] = []

  // In-place update for overwrite keys that already exist
  if (overwrite) {
    for (const section of sections) {
      for (const v of section.vars) {
        if (overwrite.has(v.key) && existingKeys.has(v.key)) {
          const pattern = new RegExp(`^${v.key}=.*$`, 'm')
          const replacement = v.comment
            ? `${v.key}="${v.value}"  # ${v.comment}`
            : `${v.key}="${v.value}"`
          existing = existing.replace(pattern, replacement)
          updated.push(v.key)
        }
      }
    }
  }

  if (existing.trim()) {
    lines.push('')
  }

  for (const section of sections) {
    const sectionVars = section.vars.filter((v) => {
      if (existingKeys.has(v.key)) {
        if (!updated.includes(v.key)) skipped.push(v.key)
        return false
      }
      added.push(v.key)
      return true
    })

    if (sectionVars.length === 0) continue

    lines.push(`# ${section.header}`)
    for (const v of sectionVars) {
      const line = v.comment ? `${v.key}="${v.value}"  # ${v.comment}` : `${v.key}="${v.value}"`
      lines.push(line)
    }
    lines.push('')
  }

  if (added.length > 0 || updated.length > 0) {
    const header = existing.trim()
      ? ''
      : '# ============================================\n# BetterStart Admin\n# ============================================\n'
    const content = existing.trim()
      ? `${existing.trimEnd()}\n${lines.join('\n')}`
      : header + lines.join('\n')
    fs.writeFileSync(envPath, content)
  }

  return { added, skipped, updated }
}

export function removeEnvVars(cwd: string, keys: string[]): string[] {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath) || keys.length === 0) {
    return []
  }

  const keySet = new Set(keys)
  const removed: string[] = []
  const filteredLines = fs
    .readFileSync(envPath, 'utf-8')
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#') || !trimmed.includes('=')) {
        return true
      }

      const [key] = trimmed.split('=')
      const normalizedKey = key?.trim()
      if (normalizedKey && keySet.has(normalizedKey)) {
        removed.push(normalizedKey)
        return false
      }

      return true
    })

  fs.writeFileSync(envPath, `${filteredLines.join('\n').trimEnd()}\n`, 'utf-8')
  return removed
}
